import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Settings2, Plus, Trash2, Save } from "lucide-react";
import { api, pesanGalat } from "../../../lib/api";
import InputRupiah from "../../../components/InputRupiah";
import { MDR_BASE, KELOMPOK_LABEL, Modal, kelasInput, kelasTombol, kelasTombolUtama } from "./shared";

/** Daftar Leasing & Tab Distributor (brief C.8/C.9/C.11, keputusan C2/C4/C8/C9/C10).
 *
 *  Owner (jawaban 23 Sep butir 2): "nanti bisa ku ubah di frontend kan?" — tab
 *  distributor (nama + nilai Distributor produk + daftar leasing-nya), leasing
 *  (kelompok, tab yang boleh, ejaan impor, metode pembayaran tertaut), toleransi,
 *  dan alias tipe semuanya diubah DI SINI, bukan lewat rilis kode.
 *
 *  Semua validasi yang menentukan kebenaran ada di server (`rapikan_pengaturan`):
 *  satu Distributor di dua tab, satu ejaan untuk dua leasing, satu metode untuk
 *  dua leasing DITOLAK karena membuat pencocokan ambigu; tab/leasing yang sudah
 *  dipakai klaim tidak bisa dihapus (nonaktifkan saja). Layar hanya mengirim.
 *  Simpan membawa `versi` — perubahan orang lain di antaranya tidak tertimpa
 *  diam-diam (daftar leasing dikirim utuh). */

const teksKeDaftar = (t) => String(t || "").split(",").map((x) => x.trim()).filter(Boolean);
const daftarKeTeks = (a) => (a || []).join(", ");

function kodeDariNama(nama, sudah) {
  const dasar = String(nama || "").toLowerCase().replace(/[^0-9a-z]+/g, "_").replace(/^_+|_+$/g, "") || "leasing";
  let kode = `leasing_${dasar}`;
  let n = 2;
  while (sudah.has(kode)) { kode = `leasing_${dasar}_${n}`; n += 1; }
  return kode;
}

export default function DialogPengaturan({ hak, onTutup, onTersimpan }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const ubah = hak.ubah;

  useEffect(() => {
    let batal = false;
    api.get(`${MDR_BASE}/pengaturan`).then((r) => {
      if (batal) return;
      setData(r.data);
      const p = r.data.pengaturan;
      setForm({
        versi: p.versi || 0,
        tabs: (p.tabs || []).map((t) => ({ ...t, _dist: daftarKeTeks(t.distributor), _baru: false })),
        leasing: (p.leasing || []).map((e) => ({ ...e, _alias: daftarKeTeks(e.alias), _baru: false })),
        toleransi_hari: p.toleransi_hari ?? 1,
        toleransi_nominal: p.toleransi_nominal ?? 0,
        maks_hari_aktivasi: p.maks_hari_aktivasi ?? 7,
        alias_tipe: (p.alias_tipe || []).map((a) => ({ ...a })),
      });
    }).catch((e) => { if (!batal) toast.error(pesanGalat(e, "Gagal memuat pengaturan Claim MDR")); });
    return () => { batal = true; };
  }, []);

  if (!form) {
    return (
      <Modal judul="Daftar Leasing & Tab" ikon={Settings2} onTutup={onTutup} lebar="max-w-5xl" testid="mdr-pengaturan">
        <p className="text-sm text-slate-400 py-6 text-center">Memuat pengaturan...</p>
      </Modal>
    );
  }

  const metode = (data?.metode_pembayaran || []).filter((m) => m.is_active !== false);
  const kodeTab = form.tabs.map((t) => (t.kode || "").trim().toUpperCase()).filter(Boolean);
  const setTab = (i, patch) => setForm((f) => ({ ...f, tabs: f.tabs.map((t, j) => (j === i ? { ...t, ...patch } : t)) }));
  const setLeasing = (i, patch) => setForm((f) => ({ ...f, leasing: f.leasing.map((e, j) => (j === i ? { ...e, ...patch } : e)) }));
  const setAlias = (i, patch) => setForm((f) => ({ ...f, alias_tipe: f.alias_tipe.map((a, j) => (j === i ? { ...a, ...patch } : a)) }));

  const simpan = async () => {
    setBusy(true);
    // Kode leasing baru diturunkan dari namanya saat disimpan — stabil, dan
    // dipakai klaim sebagai kunci (namanya boleh diganti kemudian).
    const sudah = new Set(form.leasing.filter((e) => e.kode).map((e) => e.kode));
    const leasingKode = form.leasing.map((e) => {
      if (e.kode) return e;
      const kode = kodeDariNama(e.nama, sudah);
      sudah.add(kode);
      return { ...e, kode };
    });
    try {
      const body = {
        versi: form.versi,
        tabs: form.tabs.map((t) => ({
          kode: (t.kode || "").trim().toUpperCase(), nama: t.nama || "", distributor: teksKeDaftar(t._dist),
          nama_supplier: t.nama_supplier || "", tampil_harga_jual: !!t.tampil_harga_jual, aktif: t.aktif !== false,
        })),
        leasing: leasingKode.map((e) => ({
          kode: e.kode, nama: e.nama || "", nama_ekspor: e.nama_ekspor || null, kelompok: e.kelompok,
          tab: (e.tab || []).filter((k) => kodeTab.includes(k)), alias: teksKeDaftar(e._alias),
          metode_id: e.metode_id || null, metode_kode_bawaan: e.metode_kode_bawaan || null,
          nama_lama: e.nama_lama || [], aktif: e.aktif !== false,
        })),
        toleransi_hari: Number(form.toleransi_hari) || 0,
        toleransi_nominal: Number(form.toleransi_nominal) || 0,
        maks_hari_aktivasi: Number(form.maks_hari_aktivasi) || 0,
        alias_tipe: form.alias_tipe.filter((a) => (a.alias || "").trim() || (a.tipe || "").trim()),
      };
      const r = await api.put(`${MDR_BASE}/pengaturan`, body);
      toast.success(r.data.message || "Pengaturan tersimpan");
      onTersimpan?.();
      onTutup();
    } catch (e) { toast.error(pesanGalat(e, "Gagal menyimpan pengaturan")); }
    finally { setBusy(false); }
  };

  const th = "text-left px-2 py-2 font-semibold";
  const td = "px-2 py-1.5 align-top";
  const inp = `${kelasInput} h-9 w-full`;

  return (
    <Modal judul="Daftar Leasing & Tab" ikon={Settings2} onTutup={onTutup} lebar="max-w-6xl" testid="mdr-pengaturan"
           sub={ubah ? "Ubah nama tab, nilai Distributor, dan leasing per tab. Perubahan berlaku untuk semua data yang belum Siap Claim."
                     : "Hanya lihat — mengubah butuh izin Ubah Claim MDR."}>
      <div className="space-y-6">
        {/* ---------- Tab distributor ---------- */}
        <section>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Tab Distributor</p>
            {ubah && (
              <button type="button" className={kelasTombol} data-testid="mdr-peng-tab-tambah"
                      onClick={() => setForm((f) => ({ ...f, tabs: [...f.tabs, { kode: "", nama: "", _dist: "", nama_supplier: "", tampil_harga_jual: false, aktif: true, _baru: true }] }))}>
                <Plus className="w-4 h-4" /> Tambah Tab
              </button>
            )}
          </div>
          <p className="text-[11px] text-slate-500 mb-2">
            Unit masuk tab yang nilai Distributor-nya sama dengan kolom Distributor di Master Produk. Satu nilai hanya boleh di satu tab.
          </p>
          <div className="border border-slate-200 rounded-lg overflow-x-auto">
            <table className="w-full text-xs min-w-[760px]">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                <tr>
                  <th className={th}>Kode</th><th className={th}>Nama Tab</th><th className={th}>Nilai Distributor (pisah koma)</th>
                  <th className={th}>Nama Supplier (untuk CN)</th><th className={th}>Harga Jual di Invoice</th><th className={th}>Aktif</th><th />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {form.tabs.map((t, i) => (
                  <tr key={i} data-testid={`mdr-peng-tab-${i}`}>
                    <td className={td}>
                      {t._baru && ubah ? (
                        <input value={t.kode} onChange={(e) => setTab(i, { kode: e.target.value.toUpperCase() })} className={inp}
                               placeholder="mis. ERA" data-testid={`mdr-peng-tab-kode-${i}`} />
                      ) : <span className="font-mono font-semibold">{t.kode}</span>}
                    </td>
                    <td className={td}><input value={t.nama || ""} disabled={!ubah} onChange={(e) => setTab(i, { nama: e.target.value })}
                                              className={inp} data-testid={`mdr-peng-tab-nama-${i}`} /></td>
                    <td className={td}><input value={t._dist} disabled={!ubah} onChange={(e) => setTab(i, { _dist: e.target.value })}
                                              className={inp} placeholder="TAM" data-testid={`mdr-peng-tab-dist-${i}`} /></td>
                    <td className={td}><input value={t.nama_supplier || ""} disabled={!ubah} onChange={(e) => setTab(i, { nama_supplier: e.target.value })}
                                              className={inp} placeholder="PT ..." data-testid={`mdr-peng-tab-supplier-${i}`} /></td>
                    <td className={`${td} text-center`}>
                      <input type="checkbox" checked={!!t.tampil_harga_jual} disabled={!ubah} className="w-4 h-4 mt-2"
                             onChange={(e) => setTab(i, { tampil_harga_jual: e.target.checked })} data-testid={`mdr-peng-tab-hj-${i}`} />
                    </td>
                    <td className={`${td} text-center`}>
                      <input type="checkbox" checked={t.aktif !== false} disabled={!ubah} className="w-4 h-4 mt-2"
                             onChange={(e) => setTab(i, { aktif: e.target.checked })} data-testid={`mdr-peng-tab-aktif-${i}`} />
                    </td>
                    <td className={td}>
                      {ubah && (
                        <button type="button" title="Hapus tab (ditolak kalau sudah dipakai klaim — nonaktifkan saja)"
                                onClick={() => setForm((f) => ({ ...f, tabs: f.tabs.filter((_, j) => j !== i) }))}
                                className="p-2 rounded-lg text-rose-600 hover:bg-rose-50" data-testid={`mdr-peng-tab-hapus-${i}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ---------- Leasing ---------- */}
        <section>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Daftar Leasing / CC</p>
            {ubah && (
              <button type="button" className={kelasTombol} data-testid="mdr-peng-leasing-tambah"
                      onClick={() => setForm((f) => ({ ...f, leasing: [...f.leasing, { kode: "", nama: "", nama_ekspor: "", kelompok: "non_prioritas", tab: [...kodeTab], _alias: "", metode_id: null, metode_kode_bawaan: null, nama_lama: [], aktif: true, _baru: true }] }))}>
                <Plus className="w-4 h-4" /> Tambah Leasing
              </button>
            )}
          </div>
          <p className="text-[11px] text-slate-500 mb-2">
            Leasing dicocokkan ke nota lewat METODE PEMBAYARAN tertautnya (nama lama metode yang di-rename ikut terbaca).
            Ejaan lain = cara leasing ditulis di berkas Data Claim (spasi, titik, huruf besar-kecil sudah diabaikan).
          </p>
          <div className="border border-slate-200 rounded-lg overflow-x-auto">
            <table className="w-full text-xs min-w-[980px]">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                <tr>
                  <th className={th}>Nama</th><th className={th}>Nama di Ekspor</th><th className={th}>Kelompok</th>
                  <th className={th}>Diproses di Tab</th><th className={th}>Ejaan Lain (pisah koma)</th>
                  <th className={th}>Metode Pembayaran</th><th className={th}>Aktif</th><th />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {form.leasing.map((e, i) => (
                  <tr key={e.kode || `baru-${i}`} data-testid={`mdr-peng-leasing-${e.kode || i}`}>
                    <td className={td}>
                      <input value={e.nama || ""} disabled={!ubah} className={inp} data-testid={`mdr-peng-leasing-nama-${i}`}
                             onChange={(ev) => setLeasing(i, { nama: ev.target.value })} />
                      {(e.nama_metode_dikenali || []).length > 0 && (
                        <p className="text-[10px] text-slate-400 mt-0.5">Nota: {e.nama_metode_dikenali.join(" / ")}</p>
                      )}
                    </td>
                    <td className={td}><input value={e.nama_ekspor || ""} disabled={!ubah} className={inp}
                                              onChange={(ev) => setLeasing(i, { nama_ekspor: ev.target.value })}
                                              data-testid={`mdr-peng-leasing-ekspor-${i}`} /></td>
                    <td className={td}>
                      <select value={e.kelompok} disabled={!ubah} className={inp} data-testid={`mdr-peng-leasing-kelompok-${i}`}
                              onChange={(ev) => setLeasing(i, { kelompok: ev.target.value })}>
                        {(data?.kelompok || Object.keys(KELOMPOK_LABEL)).map((k) => <option key={k} value={k}>{KELOMPOK_LABEL[k] || k}</option>)}
                      </select>
                    </td>
                    <td className={td}>
                      <div className="flex flex-wrap gap-2 pt-2">
                        {kodeTab.map((k) => (
                          <label key={k} className="inline-flex items-center gap-1">
                            <input type="checkbox" disabled={!ubah} checked={(e.tab || []).includes(k)}
                                   data-testid={`mdr-peng-leasing-tab-${i}-${k}`}
                                   onChange={(ev) => setLeasing(i, { tab: ev.target.checked ? [...(e.tab || []), k] : (e.tab || []).filter((x) => x !== k) })} />
                            {k}
                          </label>
                        ))}
                      </div>
                    </td>
                    <td className={td}><input value={e._alias} disabled={!ubah} className={inp}
                                              onChange={(ev) => setLeasing(i, { _alias: ev.target.value })}
                                              data-testid={`mdr-peng-leasing-alias-${i}`} /></td>
                    <td className={td}>
                      <select value={e.metode_id || ""} disabled={!ubah} className={inp} data-testid={`mdr-peng-leasing-metode-${i}`}
                              onChange={(ev) => setLeasing(i, { metode_id: ev.target.value || null })}>
                        <option value="">{e.metode_kode_bawaan ? "(metode bawaan)" : "— belum ditautkan —"}</option>
                        {metode.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                      </select>
                    </td>
                    <td className={`${td} text-center`}>
                      <input type="checkbox" checked={e.aktif !== false} disabled={!ubah} className="w-4 h-4 mt-2"
                             onChange={(ev) => setLeasing(i, { aktif: ev.target.checked })} data-testid={`mdr-peng-leasing-aktif-${i}`} />
                    </td>
                    <td className={td}>
                      {ubah && e._baru && (
                        <button type="button" onClick={() => setForm((f) => ({ ...f, leasing: f.leasing.filter((_, j) => j !== i) }))}
                                className="p-2 rounded-lg text-rose-600 hover:bg-rose-50" data-testid={`mdr-peng-leasing-hapus-${i}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ---------- Aturan pencocokan ---------- */}
        <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Toleransi tanggal (hari, 0-7)</span>
            <input type="number" min={0} max={7} value={form.toleransi_hari} disabled={!ubah} data-testid="mdr-peng-tol-hari"
                   onChange={(e) => setForm({ ...form, toleransi_hari: e.target.value })} className={`${kelasInput} w-full mt-1`} />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Toleransi nominal (Rp)</span>
            <InputRupiah value={form.toleransi_nominal} disabled={!ubah} testid="mdr-peng-tol-nominal" bolehKosong
                         onChange={(n) => setForm({ ...form, toleransi_nominal: n ?? 0 })} className={`${kelasInput} w-full mt-1`} />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Maks. hari aktivasi sesudah pembelian (0-90)</span>
            <input type="number" min={0} max={90} value={form.maks_hari_aktivasi} disabled={!ubah} data-testid="mdr-peng-maks-aktivasi"
                   onChange={(e) => setForm({ ...form, maks_hari_aktivasi: e.target.value })} className={`${kelasInput} w-full mt-1`} />
          </label>
        </section>

        {/* ---------- Alias tipe ---------- */}
        <section>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Alias Tipe Produk</p>
            {ubah && (
              <button type="button" className={kelasTombol} data-testid="mdr-peng-alias-tambah"
                      onClick={() => setForm((f) => ({ ...f, alias_tipe: [...f.alias_tipe, { alias: "", tipe: "" }] }))}>
                <Plus className="w-4 h-4" /> Tambah Alias
              </button>
            )}
          </div>
          <p className="text-[11px] text-slate-500 mb-2">
            Singkatan di berkas Data Claim / Master SRP (mis. "IP 17 PM" = "IPHONE 17 PRO MAX"). Pencocokan persis — tidak ada mirip-miripan.
          </p>
          {form.alias_tipe.length === 0 ? (
            <p className="text-xs text-slate-400">Belum ada alias.</p>
          ) : (
            <div className="space-y-2">
              {form.alias_tipe.map((a, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`mdr-peng-alias-${i}`}>
                  <input value={a.alias} disabled={!ubah} placeholder="IP 17 PM" className={`${kelasInput} w-40`}
                         onChange={(e) => setAlias(i, { alias: e.target.value })} data-testid={`mdr-peng-alias-singkat-${i}`} />
                  <span className="text-slate-400">=</span>
                  <input value={a.tipe} disabled={!ubah} placeholder="IPHONE 17 PRO MAX" className={`${kelasInput} flex-1 min-w-[180px]`}
                         onChange={(e) => setAlias(i, { tipe: e.target.value })} data-testid={`mdr-peng-alias-tipe-${i}`} />
                  {ubah && (
                    <button type="button" onClick={() => setForm((f) => ({ ...f, alias_tipe: f.alias_tipe.filter((_, j) => j !== i) }))}
                            className="p-2 rounded-lg text-rose-600 hover:bg-rose-50" data-testid={`mdr-peng-alias-hapus-${i}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {hak.ubah && (
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <button type="button" onClick={onTutup} className={kelasTombol} data-testid="mdr-peng-batal">Batal</button>
            <button type="button" onClick={simpan} disabled={busy} className={kelasTombolUtama} data-testid="mdr-peng-simpan">
              <Save className="w-4 h-4" /> {busy ? "Menyimpan..." : "Simpan Pengaturan"}
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}
